/**
 * L'icona di un tipo di campo — una sola definizione.
 *
 * Prima viveva dentro `ProcessModelSchema.tsx` (la master dialog dello
 * userschema del modello). La tendina dei tipi nella dialog dell'utente ne
 * aveva una copia senza icone, quindi le due superfici si presentavano diverse
 * pur essendo lo stesso catalogo. Qui sta per entrambe: due elenchi dello stesso
 * insieme di tipi non possono divergere se le icone vengono da un posto solo.
 */
import { Box } from "@mui/material";
import React from "react";
import type { SvgIconComponent } from "@mui/icons-material";
import {
  Abc,
  ArrowDropDownCircle,
  AttachFile,
  CalendarMonth,
  CheckBox as CheckBoxIcon,
  Checklist,
  Functions,
  Link,
  MailOutline,
  Notes,
  Numbers,
  Paid,
  Percent,
  Phone,
  RadioButtonChecked,
  Tag,
} from "@mui/icons-material";
import type { FieldType } from "model/CustomFields";

/**
 * L'icona di ogni tipo.
 *
 * Prima erano caratteri: due lettere per il testo, un emoji per la data, una
 * cifra per il numero. Nessuna famiglia, lunghezze diverse, e l'emoji cambia
 * disegno da sistema a sistema. Queste sono della stessa famiglia, e la stessa
 * taglia — l'elenco si legge in verticale.
 */
export const TYPE_ICONS: Readonly<Record<FieldType, SvgIconComponent>> = {
  text: Abc,
  number: Numbers,
  date: CalendarMonth,
  boolean: CheckBoxIcon,
  select: ArrowDropDownCircle,
  multiselect: Checklist,
  radio: RadioButtonChecked,
  autoincrement: Tag,
  calculated: Functions,
  currency: Paid,
  percent: Percent,
  textarea: Notes,
  email: MailOutline,
  url: Link,
  phone: Phone,
  attachment: AttachFile,
};

/** L'icona di un tipo, con lo spazio che la stacca dalla sua etichetta. */
const TypeIcon: React.FC<{ type: FieldType }> = ({ type }) => {
  const Icona = TYPE_ICONS[type];
  if (!Icona) return null;
  return (
    <Box
      component="span"
      sx={{
        mr: 1,
        // `inline-flex` e non `flex`: la tendina chiusa ridisegna questo
        // contenuto fuori da un flex container, dove `flex` diventa un blocco —
        // e l'icona si prende una riga, alzando il campo rispetto a quello
        // accanto. Inline sta di fianco in tutte e due le situazioni.
        display: "inline-flex",
        verticalAlign: "middle",
        color: "text.secondary",
      }}
    >
      <Icona fontSize="small" />
    </Box>
  );
};

export default TypeIcon;
